import Raven from 'raven-js';
import { useCallback, useState, useEffect, useRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { H2 } from 'foundations-components/transitional/components/H2';
import { useAvailableVideoConferenceExtensionByUserId } from 'MeetingsLib/hooks/videoConferenceExtensionHooks';
import { Flex } from 'foundations-components/components/Flex';
// @ts-expect-error untyped import
import { meetingsLinkTransferFailed as meetingsLinkTransferFailedSelector } from 'MeetingsAdmin/selectors/RequestStatusSelector';
import { fetchMeetingsUserByUserId
// @ts-expect-error untyped import
} from 'MeetingsAdmin/redux/actions/MeetingsUserActions';
import { transferMeetingsLink } from 'MeetingsAdmin/redux/actions/MeetingsLinkActions';
import { shouldUpdateVceLink } from 'MeetingsAdmin/utils/meetingsLinkValidation';
import { useDispatch, useSelector } from 'react-redux';
import OwnerSelect from './filterInputs/OwnerSelect';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function TransferOwnerModal({
  selectedRows,
  fromHubSpotUserId,
  onClose,
  onSuccess = emptyFunction,
  meetingsLinks = [],
  isEditView
}) {
  const dispatch = useDispatch();
  const linkBeingEdited = useSelector(state => state.linkBeingEdited);
  const transferMeetingsLinkFailed = useSelector(meetingsLinkTransferFailedSelector);
  const previouslySelectedUser = useRef(fromHubSpotUserId);
  const [selectedUser, setSelectedUser] = useState(null);
  const [linkIdsToUpdate, setLinkIdsToUpdate] = useState([]);
  const {
    videoConferenceApps,
    loading: loadingVideoConferenceApps
  } = useAvailableVideoConferenceExtensionByUserId({
    shouldSkip: !selectedUser,
    userId: selectedUser || fromHubSpotUserId
  });
  const setSelectedUserOption = useCallback(user => {
    setSelectedUser(user);
  }, []);
  useEffect(() => {
    const vceAppsFetchedForCurrentOwner = videoConferenceApps && previouslySelectedUser.current === selectedUser;
    if (loadingVideoConferenceApps || !videoConferenceApps || !selectedUser || vceAppsFetchedForCurrentOwner) {
      return;
    }
    const linkIds = [];
    if (isEditView) {
      if (shouldUpdateVceLink(linkBeingEdited.staged, videoConferenceApps)) {
        linkIds.push(linkBeingEdited.staged.id);
      }
    } else {
      const linksToTransfer = meetingsLinks.filter(link => selectedRows.includes(link.id));
      linksToTransfer.forEach(link => {
        if (shouldUpdateVceLink(link, videoConferenceApps)) {
          linkIds.push(link.id);
        }
      });
    }
    previouslySelectedUser.current = selectedUser;
    setLinkIdsToUpdate(linkIds);
  }, [videoConferenceApps, loadingVideoConferenceApps, linkBeingEdited, meetingsLinks, isEditView, selectedUser, selectedRows]);
  const transferLink = useCallback((selRows, selUser) => {
    if (!selUser) return;
    dispatch(transferMeetingsLink(selRows, fromHubSpotUserId, selUser, linkIdsToUpdate, isEditView)).then(() => {
      dispatch(fetchMeetingsUserByUserId(selUser)).then(() => {
        onClose();
        onSuccess();
      }).catch(error => Raven.captureException(error, {
        extra: {
          message: 'Error fetching meetings user by user id',
          userId: selUser
        }
      }));
    }).catch(error => Raven.captureException(error, {
      extra: {
        message: 'Error transferring meeting link',
        linksToTransfer: selRows.toArray(),
        targetUserId: selUser,
        fromHubSpotUserId
      }
    }));
  }, [dispatch, linkIdsToUpdate, fromHubSpotUserId, onClose, onSuccess, isEditView]);
  const renderSelectOrError = () => {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "transferOwnerModal.helpText"
      }), /*#__PURE__*/_jsx(OwnerSelect, {
        filterUserId: fromHubSpotUserId,
        fetchMeetingsLinks: emptyFunction,
        setSelectedUserOption: setSelectedUserOption,
        value: selectedUser,
        noResultsText: I18n.text('transferOwnerModal.noResults'),
        disabled: false,
        showAllUsersOption: false
      })]
    });
  };
  const confirmDisabled = !selectedUser;
  let button = /*#__PURE__*/_jsx(LoadingButton, {
    "data-selenium-test": "transfer-confirm-button",
    use: "primary",
    loading: loadingVideoConferenceApps,
    failed: transferMeetingsLinkFailed,
    disabled: confirmDisabled,
    onClick: () => transferLink(selectedRows, selectedUser),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "transferOwnerModal.transfer"
    })
  });
  if (confirmDisabled) {
    button = /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "transferOwnerModal.disabledTooltip"
      }),
      children: button
    });
  }
  return /*#__PURE__*/_jsxs(Modal, {
    "data-selenium-test": "transfer-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "transferOwnerModal.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        "data-selenium-test": "transfer-owner-close-button",
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: renderSelectOrError()
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [button, /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "transferOwnerModal.cancel"
        })
      })]
    })]
  });
}
export default TransferOwnerModal;